/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type ComponentProps,
  type ReactElement,
  type JSX,
  type ComponentClass,
  type FunctionComponent,
  type ComponentType,
} from "react";

export type CompositionComponent =
  FunctionComponent<any> | ComponentClass<any> | keyof JSX.IntrinsicElements;

export type CompositionTupleOf<
  C extends
    | CompositionComponent
    | readonly [CompositionComponent, ...CompositionComponent[]],
> = [C] extends [readonly [CompositionComponent, ...CompositionComponent[]]]
  ? C
  : [C] extends [CompositionComponent]
    ? readonly [C, ...C[]]
    : never;

type AliasComponent<
  T extends { alias?: CompositionComponent } = { alias?: CompositionComponent },
> = ComponentType<T>;

function isAliasElement(
  element: ReactElement<unknown>,
  alias: AliasComponent,
): element is ReactElement<ComponentProps<AliasComponent>, AliasComponent> {
  return element.type === alias;
}

export abstract class Builder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
  Default,
> {
  abstract readonly type: "single" | "multiple";
  protected _required?: boolean;
  protected _default?: Default;
  protected _aliases: AliasComponent[] = [];

  constructor(public readonly components: C) {}

  static ensureComponentTuple<
    C extends
      | CompositionComponent
      | readonly [CompositionComponent, ...CompositionComponent[]],
  >(component: C): CompositionTupleOf<C> {
    const list = [component].flat();
    const [first, ...rest] = list;
    if (!first) {
      throw new Error("Cannot build a composition with no components");
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return [first, ...rest] as unknown as CompositionTupleOf<C>;
  }

  getDefault(): Default | undefined {
    return this._default;
  }

  isRequired(): boolean {
    return this._required ?? false;
  }

  match(
    element: ReactElement<unknown>,
  ): element is ReactElement<ComponentProps<C[number]>, C[number]> {
    return this.components.some(
      (c) =>
        c === element.type ||
        this._aliases.some(
          (a) => isAliasElement(element, a) && element.props.alias === c,
        ),
    );
  }

  assertRequired(key: string): void {
    if (!this._required) {
      return;
    }
    const names = this.components
      .map((c) => (typeof c === "string" ? c : (c.displayName ?? c.name)))
      .join(" or ");
    throw new Error(
      `[react-ui] missing required "${key}" slot (${names}) in the children`,
    );
  }

  alias<T extends { alias?: CompositionComponent }>(
    alias: T extends { alias?: CompositionComponent }
      ? AliasComponent<T>
      : never,
  ): this {
    this._aliases.push(alias);
    return this;
  }
}
